import SceneSetup from '../modules/scene.js';
import ParticleSystem from '../modules/particles.js';
import CoordinateGenerator from '../modules/coordinates.js';
import StateManager from '../modules/stateManager.js';
import InteractionManager from '../modules/interaction.js';
import AnimationLoop from '../modules/animation.js';
import UIManager from '../modules/ui.js';
import { Painter } from '../modules/painter.js';
import emailjs from '@emailjs/browser';

class PortfolioApp {
    constructor() {
        this.sceneSetup = null;
        this.particleSystem = null;
        this.coordinateGenerator = null;
        this.stateManager = null;
        this.interactionManager = null;
        this.animationLoop = null;
        this.uiManager = null;
        this.painter = null;
    }

    async initialize() {
        // Initialize UI
        this.uiManager = new UIManager();
        this.uiManager.initialize();

        // Start loading animation
        const loadingInterval = this.uiManager.startLoadingAnimation();

        // Setup scene
        const container = document.getElementById('canvas-container');
        this.sceneSetup = new SceneSetup();
        const { scene, camera, renderer } = this.sceneSetup.initialize(container);

        // Setup particles
        this.particleSystem = new ParticleSystem(scene);
        this.particleSystem.initialize();

        // Generate coordinates
        this.coordinateGenerator = new CoordinateGenerator();
        await this.coordinateGenerator.generate();

        // Setup state manager
        this.stateManager = new StateManager(this.particleSystem, camera, {}, this.uiManager);

        // Setup interaction
        this.interactionManager = new InteractionManager(this.stateManager, this.coordinateGenerator, renderer);
        this.interactionManager.initialize();

        // Setup Painter (pass particleSystem for direct manipulation)
        this.painter = new Painter(this.stateManager, this.coordinateGenerator, scene, camera, this.particleSystem);
        
        // Setup painter cheat code listener
        this.setupPainterCheatCode();
        
        // Setup painter UI handlers
        this.setupPainterUI();

        // Setup animation loop
        this.animationLoop = new AnimationLoop(
            renderer,
            scene,
            camera,
            this.particleSystem,
            this.stateManager,
            this.interactionManager
        );

        // Complete loading
        clearInterval(loadingInterval);
        await this.uiManager.completeLoading();

        // Initialize home state with a random color override on first load
        const homeCoords = this.coordinateGenerator.getCoords('home');
        const randomColor = { r: Math.random(), g: Math.random(), b: Math.random() };
        this.stateManager.setTargets('home', homeCoords, false, randomColor);

        // Start animation
        this.animationLoop.start();

        // Handle window resize
        window.addEventListener('resize', () => {
            this.sceneSetup.handleResize();
        });

        // Expose changeState globally for HTML onclick
        window.changeState = (state) => {
            const coords = this.coordinateGenerator.getCoords(state);
            this.stateManager.setTargets(state, coords);
        };

        // Handle projects carousel navigation (item count and particle logo come from the DOM)
        const timelineItems = () => Array.from(document.querySelectorAll('.timeline-item'));
        let currentProjectIndex = 0;

        const showProject = (index, animateParticles = true) => {
            const items = timelineItems();
            if (items.length === 0) return;
            currentProjectIndex = ((index % items.length) + items.length) % items.length;

            items.forEach((item, idx) => {
                item.style.display = idx === currentProjectIndex ? 'block' : 'none';
            });

            const counter = document.getElementById('projects-counter');
            if (counter) {
                counter.textContent = `${currentProjectIndex + 1} / ${items.length}`;
            }

            if (animateParticles) {
                const logo = items[currentProjectIndex].dataset.logo;
                const coords = this.coordinateGenerator.getCoords(logo);
                if (coords && coords.length > 0) {
                    this.stateManager.setTargets('projects', coords, true);
                }
            }
        };

        window.navigateProjects = (direction) => showProject(currentProjectIndex + direction);
        showProject(0, false); // sync counter and visibility on load without moving particles

        // Arrow keys flip through projects while the Projects panel is open
        document.addEventListener('keydown', (e) => {
            if (this.stateManager.getCurrentState() !== 'projects') return;
            if (document.body.classList.contains('paint-mode')) return;
            if (['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName)) return;
            if (e.key === 'ArrowRight') window.navigateProjects(1);
            else if (e.key === 'ArrowLeft') window.navigateProjects(-1);
        });
    }

    setupPainterCheatCode() {
        let paintKeyBuffer = '';
        const paintCode = 'painttime';
        
        document.addEventListener('keydown', (e) => {
            if (e.key.length === 1) {
                paintKeyBuffer += e.key;
                if (paintKeyBuffer.length > paintCode.length) {
                    paintKeyBuffer = paintKeyBuffer.slice(-paintCode.length);
                }
                
                if (paintKeyBuffer === paintCode) {
                    this.painter.enter();
                    paintKeyBuffer = '';
                }
            }
        });
    }
    
    setupPainterUI() {
        const colorPicker = document.getElementById('paint-color');
        const strengthSlider = document.getElementById('paint-strength');
        const strengthValue = document.getElementById('paint-strength-value');
        const closeBtn = document.getElementById('paint-close');
        
        if (colorPicker) {
            colorPicker.addEventListener('input', (e) => {
                this.painter.setColor(e.target.value);
            });
        }
        
        if (strengthSlider && strengthValue) {
            strengthSlider.addEventListener('input', (e) => {
                const val = e.target.value;
                strengthValue.textContent = val;
                this.painter.setStrength(parseFloat(val));
            });
        }
        
        if (closeBtn) {
            closeBtn.addEventListener('click', () => {
                this.painter.exit();
            });
        }
    }

    setupProjectsScrollDetection() {
        const scrollContainer = document.getElementById('projects-scroll-container');
        if (!scrollContainer) return;

        scrollContainer.addEventListener('scroll', () => {
            if (this.stateManager.getCurrentState() !== 'projects') return;

            const scrollTop = scrollContainer.scrollTop;
            const itemHeight = 150; // Approximate height of each timeline item
            const projectIndex = Math.floor(scrollTop / itemHeight);

            const coords = this.coordinateGenerator.getLanguageLogo(projectIndex);
            if (coords && coords.length > 0) {
                this.stateManager.setTargets('projects', coords, true);
            }
        });
    }
}

// Contact form setup (EmailJS) using env-injected values via Parcel
function setupContactForm() {
    const form = document.getElementById('contact-form');
    const statusDiv = document.getElementById('form-status');
    if (!form || !statusDiv) return;

    // Parcel replaces these at build time from .env
    const PUBLIC_KEY = process.env.EMAILJS_PUBLIC_KEY || '';
    const SERVICE_ID = process.env.EMAILJS_SERVICE_ID || '';
    const TEMPLATE_ID = process.env.EMAILJS_TEMPLATE_ID || '';
    const CONTACT_TO_EMAIL = process.env.CONTACT_TO_EMAIL || 'ricky.sambataro@gmail.com';

    if (PUBLIC_KEY) {
        try { emailjs.init(PUBLIC_KEY); } catch (e) { console.warn('EmailJS init failed', e); }
    }

    form.addEventListener('submit', (e) => {
        e.preventDefault(); // stop page refresh

        const name = document.getElementById('name').value;
        const email = document.getElementById('email').value;
        const message = document.getElementById('message').value;

        if (!PUBLIC_KEY || !SERVICE_ID || !TEMPLATE_ID) {
            statusDiv.textContent = 'Configure EmailJS keys in .env to send.';
            statusDiv.classList.remove('hidden');
            return;
        }

        statusDiv.textContent = 'Sending...';
        statusDiv.classList.remove('hidden');

        emailjs.send(SERVICE_ID, TEMPLATE_ID, {
            to_email: CONTACT_TO_EMAIL,
            from_name: name,
            from_email: email,
            message: message
        }).then(() => {
            statusDiv.textContent = '✓ Message sent successfully!';
            form.reset();
            setTimeout(() => statusDiv.classList.add('hidden'), 5000);
        }).catch((error) => {
            console.log('Failed to send email:', error);
            statusDiv.textContent = '✗ Failed to send. Please try again.';
        });
    });
}

// Initialize app when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
    const app = new PortfolioApp();
    app.initialize();
    setupContactForm();
});
